import { CarrierVisitDirection } from '@planning/app/api'
import {
  ContainerJourneyStatus,
  ContainerJourneyStatusChip,
  ContainerJourneyStatusChipWrapper,
} from '@planning/components/molecules/ContainerJourneyStatusChip'
import { ReleaseReferenceTableColumn } from '@planning/pages/VesselVisit/Components/atoms/ReleaseReferenceTableColumn'
import { columnFilterPresets } from '@planning/pages/VesselVisit/tableConfigs/columnFilterConfigs'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { formatRailcarName } from '@planning/utils/railcar-utils'
import { useTranslate } from '@tolgee/react'
import {
  tomTableColumnFilterPresets,
  TomTableEmptyCell,
  TomTableUnassignedCell,
  YesNoBadge,
} from '@tom-ui/ui'
import { MRT_ColumnDef, MRT_RowData } from 'material-react-table'
import { useMemo } from 'react'

export interface RailVisitTableOrderItemDto {
  id: string | number
  railTrack: string
  sequence: number
  railcarNo: string
  containerNo: string
  status: ContainerJourneyStatus
  approved?: string
  originalOrder?: IOrderItem
  isoCode: string
  cargoStatus?: string

  weight: number
  vgm?: number
  oog?: string | null
  cooling?: string | null
  imoClass?: string | null
  seals?: string | null

  pol: string
  pod: string
  shippingLine?: string
  consignee: string
  customer: string
  operator: string

  numericReleaseReference?: string
  nonNumericReleaseReference?: string
}

export const useRailVisitTableOrderItemColumns = (direction: CarrierVisitDirection) => {
  const { t } = useTranslate()

  const isDischarge = direction === CarrierVisitDirection.Inbound
  const isLoad = direction === CarrierVisitDirection.Outbound

  return useMemo<MRT_ColumnDef<RailVisitTableOrderItemDto>[]>(
    () => [
      {
        id: 'containerNo',
        header: t('containerNo', 'Container no.'),
        accessorKey: 'containerNo',
        size: 150,
        Cell: TomTableUnassignedCell,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'railTrack',
        header: t('railTrackNo', 'Rail track no.'),
        accessorKey: 'railTrack',
        size: 100,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'sequence',
        header: t('sequenceNo', 'Seq.'),
        accessorKey: 'sequence',
        size: 70,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'railcarNo',
        header: t('railcarNo', 'Railcar no.'),
        accessorKey: 'railcarNo',
        size: 140,
        accessorFn: row => row.railcarNo,
        Cell: cell => formatRailcarName(cell.row.original.railcarNo),
        ...tomTableColumnFilterPresets.searchOptional(),
      },

      {
        id: 'status',
        header: t('status', 'Status'),
        size: 160,
        accessorFn: row => row.status,
        Cell: cell => {
          const item = cell.row.original
          const order = item.originalOrder

          if (!order?.containerJourney)
            return <ContainerJourneyStatusChip status={item.status ?? '-'} />

          return <ContainerJourneyStatusChipWrapper containerJourney={order.containerJourney} />
        },
        ...columnFilterPresets.orderStatusMultiSelect(),
      },
      {
        id: 'isoCode',
        header: t('isoCode', 'ISO'),
        accessorKey: 'isoCode',
        size: 80,
        Cell: TomTableEmptyCell,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'weight',
        header: t('grossWeight', 'Gross Weight'),
        accessorKey: 'weight',
        size: 120,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      ...(isDischarge
        ? [
            {
              id: 'pol',
              header: t('pol', 'POL'),
              accessorKey: 'pol',
              size: 100,
              ...(tomTableColumnFilterPresets.searchOptional() as MRT_RowData),
            },
          ]
        : []),
      ...(isLoad
        ? [
            {
              id: 'pod',
              header: t('pod', 'POD'),
              accessorKey: 'pod',
              size: 100,
              ...(tomTableColumnFilterPresets.searchOptional() as MRT_RowData),
            },
          ]
        : []),
      {
        id: 'consignee',
        header: t('consignee', 'Consignee'),
        accessorKey: 'consignee',
        size: 140,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'customer',
        header: t('customer', 'Customer'),
        accessorKey: 'customer',
        size: 140,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'operator',
        header: t('operator', 'Operator'),
        accessorKey: 'operator',
        size: 120,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'approved',
        header: t('approved', 'Approved'),
        accessorKey: 'approved',
        size: 90,
        ...columnFilterPresets.yesNoSelect(),
        Cell: cell => <YesNoBadge isYes={cell.row.original.approved?.toLowerCase() === 'yes'} />,
      },
      {
        id: 'cargoStatus',
        header: t('cargoStatus', 'Cargo Status'),
        accessorKey: 'cargoStatus',
        size: 140,
        ...columnFilterPresets.fullEmptySelect(),
      },

      {
        id: 'imoClass',
        header: t('imoClass', 'IMO Class'),
        accessorKey: 'imoClass',
        size: 120,
        ...columnFilterPresets.arrayContainsValue(),
      },
      {
        id: 'oog',
        header: t('oog', 'OOG'),
        accessorKey: 'oog',
        size: 80,
        ...columnFilterPresets.yesNoSelect(),
      },
      {
        id: 'cooling',
        header: t('cooling', 'Cooling'),
        accessorKey: 'cooling',
        size: 80,
        ...columnFilterPresets.yesNoSelect(),
      },
      {
        id: 'sealNo',
        header: t('sealNo', 'Seal no.'),
        accessorKey: 'seals',
        size: 140,
        ...columnFilterPresets.arrayContainsValue(),
      },

      {
        id: 'shippingLine',
        header: t('shippingLine', 'Shipping Line'),
        accessorKey: 'shippingLine',
        size: 140,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },

      {
        id: 'releaseRef',
        header: t('releaseRef', 'Release ref.'),
        size: 160,
        accessorFn: row => row.numericReleaseReference ?? row.nonNumericReleaseReference,
        Cell: cell => (
          <ReleaseReferenceTableColumn
            numericReleaseReference={cell.row.original.numericReleaseReference}
            nonNumericReleaseReference={cell.row.original.nonNumericReleaseReference}
          />
        ),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t, isDischarge, isLoad],
  )
}
